/**
 * Pay Run Variance + Reconciliation (PR8)
 * ───────────────────────────────────────
 * Pure derivations that turn the data populated by PR4–PR7 into signal:
 *
 *   1. Labor variance severity  — estimated vs actual labor cost per job,
 *      bucketed by % drift against canonical thresholds.
 *   2. Pay-run reconciliation   — compares an append-only PayRunRecord
 *      snapshot to the resolver's CURRENT view to detect post-release
 *      drift (new field-adds, new callbacks, holdback changes, dispute
 *      filings, labor actuals entered after release).
 *
 * Hard rules:
 *   • No mutations. No localStorage. No React.
 *   • Never references customer pricing / `contractValue`.
 *   • Returns structured keys; UI owns copy and tone.
 *   • Severity buckets are stable constants — do NOT inline % thresholds.
 */
import type { PayRunView } from "./getJobPayRun";
import type { PayRunRecord } from "./recordPayRun";

// ── Severity buckets ────────────────────────────────────────────────────

export const LABOR_VARIANCE_THRESHOLDS = {
  /** |variancePct| ≥ this → "critical". */
  critical: 0.25,
  /** |variancePct| ≥ this → "warn". */
  warn: 0.10,
} as const;

export type VarianceSeverity = "none" | "info" | "warn" | "critical";

export interface LaborVarianceSignal {
  /** Has enough data to render a comparison (both est + act present). */
  comparable: boolean;
  severity: VarianceSeverity;
  estimated: number | null;
  actual: number | null;
  /** actual − estimated, in dollars. */
  variance: number | null;
  /** Signed fraction (0.10 = +10% over estimate). */
  variancePct: number | null;
  /** "over" when actual > estimated, "under" when actual < estimated. */
  direction: "over" | "under" | "flat" | null;
}

export function getLaborVarianceSignal(view: PayRunView): LaborVarianceSignal {
  const { estimated, actual, variance, variancePct } = view.labor;
  if (estimated == null || actual == null) {
    return {
      comparable: false,
      severity: "none",
      estimated,
      actual,
      variance,
      variancePct,
      direction: null,
    };
  }
  const absPct = Math.abs(variancePct ?? 0);
  let severity: VarianceSeverity = "info";
  if (absPct >= LABOR_VARIANCE_THRESHOLDS.critical) severity = "critical";
  else if (absPct >= LABOR_VARIANCE_THRESHOLDS.warn) severity = "warn";

  let direction: LaborVarianceSignal["direction"] = "flat";
  if ((variance ?? 0) > 0) direction = "over";
  else if ((variance ?? 0) < 0) direction = "under";

  return { comparable: true, severity, estimated, actual, variance, variancePct, direction };
}

// ── Reconciliation (released snapshot vs current view) ──────────────────

export type ReconciliationDriftKind =
  | "field_add_added"          // new field-add appeared after release
  | "field_add_amount_changed" // approved total changed
  | "callback_added"           // new callback registered post-release
  | "holdback_changed"         // holdback total shifted
  | "dispute_opened"           // a dispute opened after release
  | "labor_actual_logged"      // labor actuals filed after release
  | "net_releasable_drift";    // net dollars no longer match

export interface ReconciliationFinding {
  kind: ReconciliationDriftKind;
  /** Signed dollar delta (current − released). null when n/a. */
  deltaAmount: number | null;
  severity: VarianceSeverity;
}

export interface PayRunReconciliation {
  payRunId: string;
  jobId: string;
  /** No drift detected — fully reconciled. */
  reconciled: boolean;
  findings: ReconciliationFinding[];
  /** Max severity across findings; "none" when reconciled. */
  severity: VarianceSeverity;
  releasedNet: number;
  currentNet: number;
  netDelta: number;
}

function severityFromDollarDelta(delta: number, baseline: number): VarianceSeverity {
  if (baseline <= 0) return Math.abs(delta) > 0 ? "warn" : "none";
  const pct = Math.abs(delta) / baseline;
  if (pct >= LABOR_VARIANCE_THRESHOLDS.critical) return "critical";
  if (pct >= LABOR_VARIANCE_THRESHOLDS.warn) return "warn";
  if (Math.abs(delta) > 0) return "info";
  return "none";
}

function maxSeverity(arr: VarianceSeverity[]): VarianceSeverity {
  const rank: Record<VarianceSeverity, number> = { none: 0, info: 1, warn: 2, critical: 3 };
  return arr.reduce<VarianceSeverity>((m, s) => (rank[s] > rank[m] ? s : m), "none");
}

export function getPayRunReconciliation(
  record: PayRunRecord,
  currentView: PayRunView,
): PayRunReconciliation {
  const snap = record.view;
  const findings: ReconciliationFinding[] = [];

  // Field-add count drift.
  if (currentView.fieldAdds.length > snap.fieldAdds.length) {
    findings.push({
      kind: "field_add_added",
      deltaAmount: currentView.fieldAddTotal - snap.fieldAddTotal,
      severity: "warn",
    });
  }
  // Approved field-add amount drift (even with same count).
  if (currentView.fieldAddApprovedTotal !== snap.fieldAddApprovedTotal) {
    findings.push({
      kind: "field_add_amount_changed",
      deltaAmount: currentView.fieldAddApprovedTotal - snap.fieldAddApprovedTotal,
      severity: severityFromDollarDelta(
        currentView.fieldAddApprovedTotal - snap.fieldAddApprovedTotal,
        Math.max(snap.baseAmount, 1),
      ),
    });
  }
  // Callback drift.
  if (currentView.callbackDeductions.length > snap.callbackDeductions.length) {
    const blocking = currentView.callbackDeductions.some((d) => d.blocksRelease)
      && !snap.callbackDeductions.some((d) => d.blocksRelease);
    findings.push({
      kind: "callback_added",
      deltaAmount: null,
      severity: blocking ? "critical" : "warn",
    });
  }
  // Holdback drift.
  if (currentView.holdbackHeld !== snap.holdbackHeld) {
    findings.push({
      kind: "holdback_changed",
      deltaAmount: currentView.holdbackHeld - snap.holdbackHeld,
      severity: severityFromDollarDelta(
        currentView.holdbackHeld - snap.holdbackHeld,
        Math.max(snap.baseAmount, 1),
      ),
    });
  }
  // Dispute opened post-release.
  if (currentView.holdReasons.includes("open_dispute") && !snap.holdReasons.includes("open_dispute")) {
    findings.push({ kind: "dispute_opened", deltaAmount: null, severity: "critical" });
  }
  // Labor actuals filed after release.
  const hadActual = snap.labor.actual != null;
  const hasActual = currentView.labor.actual != null;
  if (!hadActual && hasActual) {
    findings.push({
      kind: "labor_actual_logged",
      deltaAmount: currentView.labor.actual! - (snap.labor.estimated ?? 0),
      severity: getLaborVarianceSignal(currentView).severity,
    });
  }
  // Net dollar drift (rolls everything together).
  const netDelta = currentView.netReleasable - snap.netReleasable;
  if (netDelta !== 0) {
    findings.push({
      kind: "net_releasable_drift",
      deltaAmount: netDelta,
      severity: severityFromDollarDelta(netDelta, Math.max(snap.netReleasable, 1)),
    });
  }

  return {
    payRunId: record.id,
    jobId: record.jobId,
    reconciled: findings.length === 0,
    findings,
    severity: maxSeverity(findings.map((f) => f.severity)),
    releasedNet: snap.netReleasable,
    currentNet: currentView.netReleasable,
    netDelta,
  };
}

// ── Aggregate rollup for the cockpit Variance pivot ─────────────────────

export interface VarianceRollup {
  jobsWithLaborComparison: number;
  laborCritical: number;
  laborWarn: number;
  reconciliationDrift: number;
  reconciliationCritical: number;
  totalNetDelta: number;
}

export function getVarianceRollup(
  laborSignals: LaborVarianceSignal[],
  reconciliations: PayRunReconciliation[],
): VarianceRollup {
  return {
    jobsWithLaborComparison: laborSignals.filter((s) => s.comparable).length,
    laborCritical: laborSignals.filter((s) => s.severity === "critical").length,
    laborWarn: laborSignals.filter((s) => s.severity === "warn").length,
    reconciliationDrift: reconciliations.filter((r) => !r.reconciled).length,
    reconciliationCritical: reconciliations.filter((r) => r.severity === "critical").length,
    totalNetDelta: reconciliations.reduce((s, r) => s + r.netDelta, 0),
  };
}

export const DRIFT_KIND_LABEL: Record<ReconciliationDriftKind, string> = {
  field_add_added: "Field-add added after release",
  field_add_amount_changed: "Approved field-add total changed",
  callback_added: "New callback registered",
  holdback_changed: "Holdback amount shifted",
  dispute_opened: "Dispute opened post-release",
  labor_actual_logged: "Labor actuals logged post-release",
  net_releasable_drift: "Net releasable no longer matches",
};
